import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Toaster as ShadcnToaster } from "@/components/ui/toaster";
import { useAuthStore } from "./stores/authStore";
import { PERMISSIONS } from "./types/permissions";
import { useSharedData } from "./hooks/useSharedData";
import { shouldRedirectToLogin } from "./utils/authUtils";
import PermissionGuard from "@/components/PermissionGuard";
import LoadingSpinner from "@/components/LoadingSpinner";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/hiring/JobsList";
import Candidates from "./pages/hiring/CandidatesList";
import CandidateProfile from "./pages/hiring/CandidateProfile";
import Interviews from "./pages/hiring/InterviewsList";
import Onboarding from "./pages/hiring/Onboarding";
import HiringSettings from "./pages/hiring/HiringSettings";
import Employees from "./pages/employees/Employees";
import Departments from "./pages/employees/Departments";
import Attendance from "./pages/employees/Attendance";
import Performance from "./pages/employees/Performance";
import LeaveManagement from "@/pages/employees/LeaveManagement";
import Agreements from "@/pages/agreements/Agreements";
import Compensation from "@/pages/compensation/Compensation";
import CompanyProfile from "./pages/settings/CompanyProfile";
import UserProfile from "./pages/UserProfile";
import RolesPermissions from "@/pages/settings/RolesPermissions";
import SystemSettings from "@/pages/settings/SystemSettings";
import Invoices from '@/pages/finance/Invoices';
import Expenses from '@/pages/finance/Expenses';
import Payroll from '@/pages/finance/Payroll';
import Reports from '@/pages/finance/Reports';
import Clients from '@/pages/sales/Clients';
import Proposals from '@/pages/sales/Proposals';
import Contracts from '@/pages/sales/Contracts';
import Revenue from '@/pages/sales/Revenue';
import Projects from '@/pages/projects/Projects';
import ProjectDetail from '@/pages/projects/ProjectDetail';
import Tasks from '@/pages/projects/Tasks';
import ProjectSettings from '@/pages/projects/ProjectSettings';
import Sprints from '@/pages/projects/Sprints';
import Timeline from '@/pages/projects/Timeline';
import Resources from '@/pages/projects/Resources';
import Deliverables from '@/pages/projects/Deliverables';
import Assets from '@/pages/assets/Assets';
import Licenses from '@/pages/assets/Licenses';
import Maintenance from '@/pages/assets/Maintenance';

import Unauthorized from './pages/Unauthorized';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, initializeAuth, logout } = useAuthStore();
  const { isInitializing, progress } = useSharedData();

  // Initialize auth state on mount
  React.useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Check token validity on mount and periodically
  React.useEffect(() => {
    if (user && !isLoading) {
      // Check if tokens are valid
      const shouldRedirect = shouldRedirectToLogin();

      if (shouldRedirect) {
        logout();
        return;
      }

      // Set up periodic token validation
      const tokenValidationInterval = setInterval(() => {
        const shouldRedirect = shouldRedirectToLogin();

        if (shouldRedirect) {
          logout();
        }
      }, 30000); // Check every 30 seconds

      return () => clearInterval(tokenValidationInterval);
    }
  }, [user, isLoading, logout]);

  // Show loading state while authentication is being checked or data is initializing
  if (isLoading || isInitializing) {
    return (
      <LoadingSpinner
        text={isLoading ? "Authenticating..." : "Loading application data..."}
        showProgress={!isLoading}
        progress={progress}
        size="lg"
      />
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Employee Management Routes */}
        <Route path="employees">
          <Route index element={
            <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
              <Employees />
            </PermissionGuard>
          } />
          <Route path="departments" element={
            <PermissionGuard requiredPermission={PERMISSIONS.DEPARTMENT_VIEW}>
              <Departments />
            </PermissionGuard>
          } />
          <Route path="attendance" element={
            <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
              <Attendance />
            </PermissionGuard>
          } />
          <Route path="performance" element={
            <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
              <Performance />
            </PermissionGuard>
          } />
          <Route path="leaves" element={
            <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
              <LeaveManagement />
            </PermissionGuard>
          } />
        </Route>

        {/* Agreements Route */}
        <Route path="agreements" element={
          <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
            <Agreements />
          </PermissionGuard>
        } />

        {/* Compensation Route */}
        <Route path="compensation" element={
          <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
            <Compensation />
          </PermissionGuard>
        } />

        {/* Hiring Management Routes */}
        <Route path="hiring">
          <Route path="candidates" element={
            <PermissionGuard requiredPermission={PERMISSIONS.CANDIDATE_VIEW}>
              <Candidates />
            </PermissionGuard>
          } />
          <Route path="candidates/:id" element={
            <PermissionGuard requiredPermission={PERMISSIONS.CANDIDATE_VIEW}>
              <CandidateProfile />
            </PermissionGuard>
          } />
          <Route path="jobs" element={
            <PermissionGuard requiredPermission={PERMISSIONS.JOB_VIEW}>
              <Jobs />
            </PermissionGuard>
          } />
          <Route path="interviews" element={
            <PermissionGuard requiredPermission={PERMISSIONS.EMPLOYEES_VIEW}>
              <Interviews />
            </PermissionGuard>
          } />
          <Route path="onboarding" element={
            <PermissionGuard requiredModulePermission={{ module: 'hiring', action: 'view' }}>
              <Onboarding />
            </PermissionGuard>
          } />
          <Route path="settings" element={
            <PermissionGuard requiredPermission={PERMISSIONS.SYSTEM_ADMIN}>
              <HiringSettings />
            </PermissionGuard>
          } />
        </Route>

        {/* Project Management Routes */}
        <Route path="projects">
          <Route index element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <Projects />
            </PermissionGuard>
          } />
          <Route path=":id" element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <ProjectDetail />
            </PermissionGuard>
          } />
          <Route path="tasks" element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <Tasks />
            </PermissionGuard>
          } />
          <Route path="settings" element={
            <PermissionGuard requiredSubModulePermission={{ module: 'project_management', subModule: 'projectStatus', action: 'view' }}>
              <ProjectSettings />
            </PermissionGuard>
          } />
          <Route path="sprints" element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <Sprints />
            </PermissionGuard>
          } />
          <Route path="timeline" element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <Timeline />
            </PermissionGuard>
          } />
          <Route path="resources" element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <Resources />
            </PermissionGuard>
          } />
          <Route path="deliverables" element={
            <PermissionGuard requiredModulePermission={{ module: 'project_management', action: 'view' }}>
              <Deliverables />
            </PermissionGuard>
          } />
        </Route>

        {/* Sales & Clients Routes */}
        <Route path="sales">
          <Route index element={
            <PermissionGuard requiredPermission={PERMISSIONS.SALES_VIEW}>
              <Clients />
            </PermissionGuard>
          } />
          <Route path="clients" element={
            <PermissionGuard requiredPermission={PERMISSIONS.SALES_VIEW}>
              <Clients />
            </PermissionGuard>
          } />
          <Route path="proposals" element={
            <PermissionGuard requiredPermission={PERMISSIONS.PROPOSALS_VIEW}>
              <Proposals />
            </PermissionGuard>
          } />
          <Route path="contracts" element={
            <PermissionGuard requiredPermission={PERMISSIONS.CONTRACTS_VIEW}>
              <Contracts />
            </PermissionGuard>
          } />
          <Route path="revenue" element={
            <PermissionGuard requiredPermission={PERMISSIONS.REVENUE_VIEW}>
              <Revenue />
            </PermissionGuard>
          } />
        </Route>

        {/* Asset Management Routes */}
        <Route path="assets">
          <Route index element={
            <PermissionGuard requiredPermission={PERMISSIONS.SYSTEM_ADMIN}>
              <Assets />
            </PermissionGuard>
          } />
          <Route path="licenses" element={
            <PermissionGuard requiredPermission={PERMISSIONS.SYSTEM_ADMIN}>
              <Licenses />
            </PermissionGuard>
          } />
          <Route path="maintenance" element={
            <PermissionGuard requiredPermission={PERMISSIONS.SYSTEM_ADMIN}>
              <Maintenance />
            </PermissionGuard>
          } />
        </Route>

        {/* Finance Routes */}
        <Route path="finance">
          <Route index element={
            <PermissionGuard requiredPermission={PERMISSIONS.INVOICE_VIEW}>
              <Invoices />
            </PermissionGuard>
          } />
          <Route path="invoices" element={
            <PermissionGuard requiredPermission={PERMISSIONS.INVOICE_VIEW}>
              <Invoices />
            </PermissionGuard>
          } />
          <Route path="expenses" element={
            <PermissionGuard requiredPermission={PERMISSIONS.EXPENSE_VIEW}>
              <Expenses />
            </PermissionGuard>
          } />
          <Route path="payroll" element={
            <PermissionGuard superAdminOnly>
              <Payroll />
            </PermissionGuard>
          } />
          <Route path="reports" element={
            <PermissionGuard requiredSubModulePermission={{ module: 'finance', subModule: 'financialReport', action: 'view' }}>
              <Reports />
            </PermissionGuard>
          } />
        </Route>

        {/* Settings Routes */}
        <Route path="settings">
          <Route index element={
            <PermissionGuard requiredPermission={PERMISSIONS.USER_VIEW}>
              <CompanyProfile />
            </PermissionGuard>
          } />
          <Route path="company-profile" element={
            <PermissionGuard requiredPermission={PERMISSIONS.USER_VIEW}>
              <CompanyProfile />
            </PermissionGuard>
          } />
          <Route path="roles" element={
            <PermissionGuard requiredPermission={PERMISSIONS.ROLE_VIEW}>
              <RolesPermissions />
            </PermissionGuard>
          } />
          <Route path="system" element={
            <PermissionGuard requiredPermission={PERMISSIONS.SYSTEM_ADMIN}>
              <SystemSettings />
            </PermissionGuard>
          } />
        </Route>
        <Route path="unauthorized" element={<Unauthorized />} />
      </Route>

      {/* User Profile */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <UserProfile />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Employee Profile */}
      <Route
        path="/employee/:employeeId"
        element={
          <ProtectedRoute>
            <Layout>
              <UserProfile />
            </Layout>
          </ProtectedRoute>
        }
      />



      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <div className="min-h-screen bg-background">
        <AppRoutes />
        <Toaster
          position="bottom-right"
          richColors
          toastOptions={{
            style: {
              borderRadius: '12px',
              padding: '12px 16px',
            },
            classNames: {
              success: '!bg-white !text-green-700 !border-green-200',
              error: '!bg-red-50 !text-red-700 !border-red-200',
              info: '!bg-blue-50 !text-blue-700 !border-blue-200',
            }
          }}
        />
        <ShadcnToaster />
      </div>
    </Router>
  );
}
